import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

/**
 * CategoryFilter Component
 * 
 * Reusable filter component for memory categories with domain, severity, and importance filters.
 */
const CategoryFilter = ({ filters, onFiltersChange, onClearFilters }) => {
  const domains = [
    { value: 'communications', label: 'Communications' },
    { value: 'gross_motor', label: 'Gross Motor' },
    { value: 'fine_motor', label: 'Fine Motor' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'personal_social', label: 'Personal/Social' },
    { value: 'general', label: 'General' }
  ];

  const severities = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'none', label: 'None' }
  ];

  const handleDomainChange = (value) => {
    onFiltersChange({ ...filters, domain: value === 'all' ? null : value });
  };

  const handleSeverityChange = (value) => {
    onFiltersChange({ ...filters, severity: value === 'all' ? null : value });
  };

  const handleImportanceChange = (value) => {
    onFiltersChange({ ...filters, min_importance: value[0] });
  };

  const hasActiveFilters = filters.domain || filters.severity || filters.min_importance > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Filters</CardTitle>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-6 px-2 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs">Domain</Label>
          <Select
            value={filters.domain || 'all'}
            onValueChange={handleDomainChange}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All domains" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Domains</SelectItem>
              {domains.map((domain) => (
                <SelectItem key={domain.value} value={domain.value}>
                  {domain.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Severity</Label>
          <Select
            value={filters.severity || 'all'}
            onValueChange={handleSeverityChange}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              {severities.map((severity) => (
                <SelectItem key={severity.value} value={severity.value}>
                  {severity.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">
            Minimum Importance: {(filters.min_importance || 0).toFixed(1)}
          </Label>
          <Slider
            value={[filters.min_importance || 0]}
            onValueChange={handleImportanceChange}
            min={0}
            max={1}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>0.0</span>
            <span>1.0</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryFilter;

